import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { PersistentStreamResponse, CacheInfo } from '@shared/types';
import { API_BASE_URL } from '@/client/config';
import { handleApiError } from '@/client/lib/apiError';

export interface PersistentStreamWithClient extends PersistentStreamResponse {
  clientName: string;
}

interface AllPersistentStreamsState {
  streams: PersistentStreamWithClient[];
  loading: boolean;
  error: string | null;
  lastFetched: number | null;
  cacheInfo: CacheInfo | null;
  setStreams: (streams: PersistentStreamWithClient[]) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  fetchStreams: (accessToken: string, logout: () => void, forceRefresh?: boolean) => Promise<void>;
  invalidateCache: () => void;
}

export const useAllPersistentStreamsStore = create<AllPersistentStreamsState>()(devtools((set, get) => ({
  streams: [],
  loading: false,
  error: null,
  lastFetched: null,
  cacheInfo: null,
  setStreams: (streams) => set({ streams, error: null }),
  setLoading: (loading) => set({ loading }),
  setError: (error) => set({ error }),
  invalidateCache: () => set({ streams: [], lastFetched: null, cacheInfo: null }),
  fetchStreams: async (accessToken: string, logout: () => void, forceRefresh = false) => {
    const state = get();

    // If we have cached data and not forcing refresh, skip the fetch
    if (!forceRefresh && state.streams.length > 0 && state.lastFetched) {
      return;
    }

    set({ loading: true, error: null });
    try {
      const response = await fetch(`${API_BASE_URL}/api/persistent-streams`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (handleApiError(response, logout)) return;
        throw new Error(`Failed to fetch persistent streams: ${response.statusText}`);
      }

      const data = await response.json();
      set({
        streams: data.persistentStreams,
        lastFetched: Date.now(),
        loading: false,
        cacheInfo: data.cacheInfo ?? null,
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      set({ error: errorMessage, loading: false });
    }
  },
}), { name: 'all-persistent-streams-store' }));
